/**
 * T278 — client-side square crop for member avatars. Takes the pixel region the
 * member chose in the crop UI and renders it to a fixed-size square canvas,
 * exporting a JPEG blob. Producing a square image up-front means the stored
 * avatar never gets awkwardly center-cropped by the circular `object-fit: cover`
 * frame. Browser-only (uses Image + canvas); never imported server-side.
 */

export type PixelCrop = { x: number; y: number; width: number; height: number };

/** Stored avatar edge length (px). 512² keeps retina sharpness at ~88px display. */
const AVATAR_OUTPUT_SIZE = 512;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", () => reject(new Error("AVATAR_IMAGE_LOAD_FAILED")));
    img.src = src;
  });
}

/**
 * Prefer WEBP (~30% smaller than JPEG at equal quality) when the canvas can
 * encode it; fall back to JPEG on browsers that can't (older Safari). Both are
 * in the server's accepted MIME set, so either passes the upload gate.
 */
function pickOutputFormat(): { type: string; quality: number } {
  try {
    const probe = document.createElement("canvas");
    if (probe.toDataURL("image/webp").startsWith("data:image/webp")) {
      return { type: "image/webp", quality: 0.85 };
    }
  } catch {
    /* fall through to JPEG */
  }
  return { type: "image/jpeg", quality: 0.9 };
}

/**
 * Render the chosen square region to a fixed 512² canvas and export an
 * optimized blob. The output is always tiny (~50-150 KB), so a multi-MB phone
 * photo is downscaled here and never uploaded at full size — the upload-size
 * gate only ever sees the small result.
 */
export async function getCroppedSquareImage(
  imageSrc: string,
  crop: PixelCrop,
  outputSize: number = AVATAR_OUTPUT_SIZE,
): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("AVATAR_CANVAS_UNAVAILABLE");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, outputSize, outputSize);
  const { type, quality } = pickOutputFormat();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("AVATAR_CANVAS_EMPTY"))),
      type,
      quality,
    );
  });
}
